import { create } from "zustand";

export type FileUploadStatus = "queued" | "processing" | "done" | "error" | "skipped";

/** Статус обработки одного листа внутри мульти-листового файла */
export interface UploadSheetState {
	sheetIndex: number;
	sheetName: string;
	status: FileUploadStatus;
	fileId?: number;
	fileType?: string;
	error?: string;
}

export interface UploadFileState {
	filename: string;
	status: FileUploadStatus;
	fileId?: number;
	fileType?: string;
	objectName?: string | null;
	periodMonth?: number | null;
	periodYear?: number | null;
	sheetName?: string | null;
	error?: string;
	/** Статусы отдельных листов (для мульти-листовой загрузки) */
	sheetStatuses?: UploadSheetState[];
}

/** Результат парсинга (книга расценок или наряд) */
export interface ParsingResult {
	fileIndex: number;
	fileType: "rate_book" | "work_order" | "completion_act";
	totalRows: number;
	parsedCount: number;
	skippedCount: number;
	errors: { row: number; reason: string }[];
	/**
	 * Результат валидации акта v3 (только для `fileType === "completion_act"`).
	 * Содержит флаги арифметики, счётчики issues/warnings и первые ≤10 issues.
	 */
	completionActValidation?: {
		arithmeticOk: boolean;
		subtotalsOk: boolean;
		grandTotalOk: boolean;
		blocksCount: number;
		worksCount: number;
		materialsCount: number;
		extraLaborCount: number;
		issuesCount: number;
		warningsCount: number;
		layoutVariant: "standard" | "with_code_in_a" | "without_code";
		fileStatus: "parsed" | "parsed_with_warnings";
		issuesPreview: Array<{
			kind: string;
			scope: "work" | "material" | "block" | "grand";
			row?: number;
			/** Доп. строки продолжения работы (только для scope="work"). */
			continuationRows?: number[];
			expected?: number;
			got?: number;
			delta?: number;
			message: string;
		}>;
	};
}

/** Данные для диалога конфликта rate_book (единственный оставшийся тип конфликта) */
export interface PendingConflict {
	uploadId: string;
	fileIndex: number;
	filename: string;
	conflictType: "rate-book";
	existingFile: {
		id: number;
		originalName: string;
		fileType: string;
		createdAt: string;
	};
}

/** Данные для диалога выбора листов.
 * `alreadyLoaded` — имена листов этого файла, уже загруженных ранее (показываются серыми). */
export interface PendingSheetSelect {
	uploadId: string;
	fileIndex: number;
	filename: string;
	sheets: string[];
	alreadyLoaded?: string[];
}

interface UploadState {
	/** Файлы выбранные для загрузки */
	selectedFiles: File[];
	/** ID текущей загрузки */
	uploadId: string | null;
	/** Статус каждого файла в пакете */
	fileStatuses: UploadFileState[];
	/** Загрузка в процессе */
	isUploading: boolean;
	/** Общее количество файлов */
	totalFiles: number;
	/** Количество обработанных */
	completedFiles: number;
	/** Ожидающий выбор листа */
	pendingSheetSelect: PendingSheetSelect | null;
	/** Ожидающий конфликт (дубликат или rate_book) */
	pendingConflict: PendingConflict | null;
	/** Результаты парсинга книги расценок */
	parsingResults: ParsingResult[];

	/** Выбрать файлы для загрузки — поднимает isUploading сразу, чтобы залочить кнопку */
	setSelectedFiles: (files: File[]) => void;
	/** Зафиксировать серверный uploadId после успешного POST */
	startUpload: (uploadId: string) => void;
	/** Обновить статус файла при получении SSE */
	updateFileStatus: (fileIndex: number, update: Partial<UploadFileState>) => void;
	/** Обновить/добавить статус отдельного листа (мульти-листовой режим) */
	upsertSheetStatus: (
		fileIndex: number,
		sheet: Omit<UploadSheetState, "status"> & Partial<Pick<UploadSheetState, "status">>,
	) => void;
	/** Установить ожидающий выбор листа */
	setPendingSheetSelect: (data: PendingSheetSelect | null) => void;
	/** Установить ожидающий конфликт */
	setPendingConflict: (data: PendingConflict | null) => void;
	/** Добавить результат парсинга */
	addParsingResult: (result: ParsingResult) => void;
	/** Завершить загрузку */
	completeUpload: () => void;
	/** Пометить все queued-файлы как error и завершить загрузку, сохранив статусы для показа пользователю */
	failUpload: (reason: string) => void;
	/** Обновить суммарный прогресс (total/completed) — вызывается при events progress-update/sheet-done */
	setProgress: (total: number, completed: number) => void;
	/** Сбросить состояние */
	reset: () => void;
}

export const useUploadStore = create<UploadState>((set) => ({
	selectedFiles: [],
	uploadId: null,
	fileStatuses: [],
	isUploading: false,
	totalFiles: 0,
	completedFiles: 0,
	pendingSheetSelect: null,
	pendingConflict: null,
	parsingResults: [],

	setSelectedFiles: (files) =>
		set({
			selectedFiles: files,
			fileStatuses: files.map((f) => ({
				filename: f.name,
				status: "queued" as const,
			})),
			totalFiles: files.length,
			completedFiles: 0,
			parsingResults: [],
			isUploading: true,
		}),

	startUpload: (uploadId) => set({ uploadId }),

	updateFileStatus: (fileIndex, update) =>
		set((state) => {
			const statuses = [...state.fileStatuses];
			if (statuses[fileIndex]) {
				statuses[fileIndex] = { ...statuses[fileIndex], ...update };
			}
			// Не перезаписывать completedFiles, если totalFiles уже отличается от fileStatuses.length
			// (мульти-листовой режим — прогресс управляется сервером через progress-update/sheet-done).
			if (state.totalFiles !== state.fileStatuses.length) {
				return { fileStatuses: statuses };
			}
			const completedFiles = statuses.filter(
				(s) => s.status === "done" || s.status === "error" || s.status === "skipped",
			).length;
			return { fileStatuses: statuses, completedFiles };
		}),

	upsertSheetStatus: (fileIndex, sheet) =>
		set((state) => {
			const statuses = [...state.fileStatuses];
			const file = statuses[fileIndex];
			if (!file) return {};
			const sheets = file.sheetStatuses ? [...file.sheetStatuses] : [];
			const existingIdx = sheets.findIndex((s) => s.sheetIndex === sheet.sheetIndex);
			const merged: UploadSheetState = {
				sheetIndex: sheet.sheetIndex,
				sheetName: sheet.sheetName,
				status: sheet.status ?? sheets[existingIdx]?.status ?? "processing",
				fileId: sheet.fileId ?? sheets[existingIdx]?.fileId,
				fileType: sheet.fileType ?? sheets[existingIdx]?.fileType,
				error: sheet.error ?? sheets[existingIdx]?.error,
			};
			if (existingIdx >= 0) {
				sheets[existingIdx] = merged;
			} else {
				sheets.push(merged);
			}
			statuses[fileIndex] = { ...file, sheetStatuses: sheets };
			return { fileStatuses: statuses };
		}),

	setPendingSheetSelect: (data) => set({ pendingSheetSelect: data }),

	setPendingConflict: (data) => set({ pendingConflict: data }),

	addParsingResult: (result) =>
		set((state) => ({
			parsingResults: [...state.parsingResults, result],
		})),

	completeUpload: () =>
		set((state) => {
			const hasIssues = state.fileStatuses.some(
				(f) => f.status === "error" || f.status === "skipped",
			);
			return {
				isUploading: false,
				uploadId: null,
				selectedFiles: [],
				// Сохраняем fileStatuses при наличии ошибок/пропусков, чтобы пользователь увидел результат
				fileStatuses: hasIssues ? state.fileStatuses : [],
				totalFiles: hasIssues ? state.totalFiles : 0,
				completedFiles: hasIssues ? state.completedFiles : 0,
				pendingSheetSelect: null,
				pendingConflict: null,
			};
		}),

	setProgress: (total, completed) => set({ totalFiles: total, completedFiles: completed }),

	failUpload: (reason) =>
		set((state) => {
			const statuses = state.fileStatuses.map((f) =>
				f.status === "queued" || f.status === "processing"
					? { ...f, status: "error" as const, error: reason }
					: f,
			);
			const completedFiles = statuses.filter(
				(s) => s.status === "done" || s.status === "error" || s.status === "skipped",
			).length;
			return {
				isUploading: false,
				uploadId: null,
				selectedFiles: [],
				fileStatuses: statuses,
				completedFiles,
				pendingSheetSelect: null,
				pendingConflict: null,
			};
		}),

	reset: () =>
		set({
			selectedFiles: [],
			uploadId: null,
			fileStatuses: [],
			isUploading: false,
			totalFiles: 0,
			completedFiles: 0,
			pendingSheetSelect: null,
			pendingConflict: null,
		}),
}));
